import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { mfaEnroll, mfaEnrollVerify, getMFADevices } from '../services/api';
import { useEntitlements } from '../store/EntitlementStore';
import { Shield, AlertCircle, CheckCircle2, Copy } from 'lucide-react';
import './MFAEnrollmentPage.css';

/**
 * MFA Enrollment Page - Full-page enrollment for superusers without MFA configured.
 * This page is required and cannot be skipped.
 */
export const MFAEnrollmentPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, fetchCurrentUser } = useEntitlements();
  const [step, setStep] = useState('name'); // 'name', 'qr', 'verify', 'recovery'
  const [deviceName, setDeviceName] = useState('');
  const [qrCode, setQrCode] = useState(null);
  const [secret, setSecret] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [recoveryCodes, setRecoveryCodes] = useState(null);
  const [checkingStatus, setCheckingStatus] = useState(true);

  // Check if user is superuser and if MFA enrollment is needed
  useEffect(() => {
    const checkMFAStatus = async () => {
      if (!user) {
        setCheckingStatus(false);
        return;
      }

      // Only superusers should access this page
      if (!user.is_superuser) {
        navigate('/');
        return;
      }

      try {
        // Check if user already has MFA enrolled
        const response = await getMFADevices();
        // Handle both paginated (results) and non-paginated (devices array) responses
        const devices = response.data.results || response.data.devices || response.data || [];
        const hasEnabledDevice = Array.isArray(devices) && devices.some(d => d.is_enabled);

        // If already enrolled, redirect away
        if (hasEnabledDevice) {
          // Redirect to intended destination or platform admin
          const intendedDestination = location.state?.from || 
                                     new URLSearchParams(window.location.search).get('from') || 
                                     '/platform/admin';
          navigate(intendedDestination, { replace: true });
          return;
        }
      } catch (err) {
        console.error('Failed to check MFA status:', err);
        // Continue with enrollment page even if check fails
      } finally {
        setCheckingStatus(false);
      }
    };

    checkMFAStatus();
  }, [user, navigate, location]);

  const handleStartEnrollment = async (e) => {
    e.preventDefault();
    if (!deviceName.trim()) {
      setError('Device name is required');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await mfaEnroll(deviceName.trim());
      setQrCode(response.data.qr_code);
      setSecret(response.data.secret);
      setStep('qr');
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.detail || 'Failed to start enrollment');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!verificationCode.trim()) {
      setError('Verification code is required');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await mfaEnrollVerify(verificationCode.trim());
      
      // Check if recovery codes were returned
      if (response.data.recovery_codes) {
        setRecoveryCodes(response.data.recovery_codes);
        setStep('recovery');
      } else {
        // Enrollment successful, no recovery codes shown
        handleEnrollmentComplete();
      }
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.detail || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleEnrollmentComplete = async () => {
    // Refresh user data to get updated MFA status
    await fetchCurrentUser();
    
    // Redirect to intended destination or platform admin
    const intendedDestination = location.state?.from || 
                               new URLSearchParams(window.location.search).get('from') || 
                               '/platform/admin';
    navigate(intendedDestination, { replace: true });
  };

  const handleCopySecret = () => {
    navigator.clipboard.writeText(secret);
  };

  const handleCopyRecoveryCodes = () => {
    if (!recoveryCodes || recoveryCodes.length === 0) return;
    const text = recoveryCodes.join('\n');
    navigator.clipboard.writeText(text);
  };

  const handleDownloadRecoveryCodes = () => {
    if (!recoveryCodes || recoveryCodes.length === 0) return;
    const content = recoveryCodes.join('\n');
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'mfa-recovery-codes.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Show loading while checking status
  if (checkingStatus) {
    return (
      <div className="mfa-enrollment-page">
        <div className="mfa-enrollment-container">
          <div className="loading-spinner">Checking MFA status...</div>
        </div>
      </div>
    );
  }

  // Verify user is superuser
  if (!user || !user.is_superuser) {
    return (
      <div className="mfa-enrollment-page">
        <div className="mfa-enrollment-container">
          <div className="mfa-enrollment-error">
            <AlertCircle size={24} />
            <h2>Access Denied</h2>
            <p>This page is only accessible to superusers.</p>
            <button onClick={() => navigate('/')} className="btn-primary">
              Return to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mfa-enrollment-page">
      <div className="mfa-enrollment-container">
        <div className="mfa-enrollment-header">
          <Shield size={32} />
          <h1>Multi-Factor Authentication Required</h1>
          <p className="mfa-enrollment-subtitle">
            As a superuser, you must set up multi-factor authentication to access the platform.
            This is a security requirement and cannot be skipped.
          </p>
        </div>

        {error && (
          <div className="mfa-enrollment-error-banner">
            <AlertCircle size={16} />
            {error}
          </div>
        )}

        {/* Step 1: Device Name */}
        {step === 'name' && (
          <div className="mfa-enrollment-step">
            <div className="mfa-enrollment-step-header">
              <h2>Step 1: Name Your Device</h2>
              <p>Enter a name for this device to help you identify it later.</p>
            </div>
            <form onSubmit={handleStartEnrollment} className="mfa-enrollment-form">
              <div className="form-group">
                <label>Device Name *</label>
                <input
                  type="text"
                  value={deviceName}
                  onChange={(e) => setDeviceName(e.target.value)}
                  placeholder="My iPhone"
                  required
                  autoFocus
                  disabled={loading}
                />
                <small>Examples: "iPhone", "Work Laptop", "Google Authenticator"</small>
              </div>
              <div className="mfa-enrollment-actions">
                <button type="submit" className="btn-primary" disabled={loading}>
                  {loading ? 'Starting...' : 'Continue'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Step 2: QR Code Display */}
        {step === 'qr' && (
          <div className="mfa-enrollment-step">
            <div className="mfa-enrollment-step-header">
              <h2>Step 2: Scan QR Code</h2>
              <p>Scan this QR code with your authenticator app to add your account.</p>
            </div>
            
            {qrCode && (
              <div className="mfa-qr-container">
                <img src={qrCode} alt="MFA QR Code" className="mfa-qr-code" />
              </div>
            )}

            <div className="mfa-secret-container">
              <label>Or enter this code manually:</label>
              <div className="mfa-secret-display">
                <code className="mfa-secret-text">{secret}</code>
                <button
                  type="button"
                  onClick={handleCopySecret}
                  className="mfa-copy-button"
                  title="Copy secret"
                >
                  <Copy size={16} />
                  Copy
                </button>
              </div>
            </div>

            <div className="mfa-enrollment-instructions">
              <h4>Instructions:</h4>
              <ol>
                <li>Open your authenticator app (Google Authenticator, Authy, Microsoft Authenticator, etc.)</li>
                <li>Scan the QR code above or enter the secret code manually</li>
                <li>Enter the 6-digit code from your app below to verify</li>
              </ol>
            </div>

            <form onSubmit={handleVerify} className="mfa-enrollment-form">
              <div className="form-group">
                <label>Verification Code *</label>
                <input
                  type="text"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  maxLength={6}
                  required
                  autoFocus
                  disabled={loading}
                  className="mfa-code-input"
                />
                <small>Enter the 6-digit code from your authenticator app</small>
              </div>
              <div className="mfa-enrollment-actions">
                <button type="button" onClick={() => setStep('name')} className="btn-secondary" disabled={loading}>
                  Back
                </button>
                <button type="submit" className="btn-primary" disabled={loading || verificationCode.length !== 6}>
                  {loading ? 'Verifying...' : 'Verify & Complete'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Step 3: Recovery Codes */}
        {step === 'recovery' && recoveryCodes && (
          <div className="mfa-enrollment-step">
            <div className="mfa-recovery-success">
              <CheckCircle2 size={32} />
              <h2>MFA Successfully Enabled!</h2>
            </div>
            <div className="mfa-enrollment-step-header">
              <h3>Save Your Recovery Codes</h3>
              <p>
                <strong>Important:</strong> Save these recovery codes in a safe place. You can use them to access your account if you lose access to your authenticator device. Each code can only be used once.
              </p>
            </div>
            <div className="mfa-recovery-codes-container">
              {recoveryCodes.map((code, index) => (
                <div key={index} className="mfa-recovery-code-item">
                  <code>{code}</code>
                </div>
              ))}
            </div>
            <div className="mfa-recovery-actions">
              <button
                type="button"
                onClick={handleDownloadRecoveryCodes}
                className="btn-secondary"
              >
                Download Codes
              </button>
              <button
                type="button"
                onClick={handleCopyRecoveryCodes}
                className="btn-secondary"
              >
                Copy All
              </button>
            </div>
            <div className="mfa-enrollment-actions">
              <button type="button" onClick={handleEnrollmentComplete} className="btn-primary">
                I've Saved My Codes - Continue
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
